import { fromJS, Map, OrderedMap } from 'immutable';
import { SportMarketBetsSnapshot, SportsMenuSnapshot, SportTournamentsSnapshot } from '../../../dto/SnapshotEnvelope';
import { PushEntity, PushPayload } from '../../../dto/PushEnvelope';
import { onMatchesSnapshot, onMatchPush } from './matchHandlers';
import { onTournamentPush, onTournamentsSnapshot } from './tournamentHandlers';
import { onBetPush, onBetsSnapshotMerge } from './betHandlers';
import { onScoreboardTypesSnapshot, onSportPush, onSportsSnapshot } from './sportHandlers';
import { onOverviewMarketsSnapshot } from './marketHandlers';
import { isBetradarWidgetAvailable, isUFC } from '../../../../match-statistics/utils';
import { now } from 'moment';
import fp from 'lodash/fp';

export const clearOverview = db => db.deleteIn(['turboLive', 'overview']);

export const sportsSnapshot = (snapshot: SportsMenuSnapshot) => db => {
    return db
        .update('turboLive', Map(), onSportsSnapshot(snapshot.sports))
        .update('turboLive', Map(), onScoreboardTypesSnapshot(snapshot.scoreboardTypes || []))
        .setIn(['turboLive', 'sportsSnapshotLoaded'], true);
};

export const sportsPush = (push: PushPayload) => db => {
    return db.update('turboLive', Map(), onSportPush(push.e, push.d));
};

export const overviewTournamentsSnapshot = (sportCode: string, snapshot: SportTournamentsSnapshot) => {
    return (db: Map<string, any>) => {
        const updateFn = fp.compose(
            onTournamentsSnapshot(snapshot.tournaments),
            onMatchesSnapshot(snapshot.matches),
            onOverviewMarketsSnapshot(snapshot.markets)
        );
        return db
            .updateIn(['turboLive', 'overview', sportCode], Map(), _db => updateFn(_db))
            .setIn(['turboLive', 'overview', sportCode, 'snapshotLoaded'], true);
    };
};

export const overviewTournamentsPush = (sportCode: string, push: PushPayload) => (db: Map<string, any>) => {
    switch (push.t) {
        case PushEntity.MATCH:
            return db.updateIn(['turboLive', 'overview', sportCode], onMatchPush(push.e, push.d));
        case PushEntity.TOURNAMENT:
            return db.updateIn(['turboLive', 'overview', sportCode], onTournamentPush(push.e, push.d));
        default:
            return db;
    }
};

export const overviewBetsSnapshot = (sportCode: string, snapshot: SportMarketBetsSnapshot) => db => {
    return db.updateIn(
        ['turboLive', 'overview', sportCode],
        Map(),
        onBetsSnapshotMerge(snapshot.marketId, snapshot.bets)
    );
};

export const overviewBetsPush = (sportCode: string, push: PushPayload) => db => {
    return db.updateIn(['turboLive', 'overview', sportCode], Map(), onBetPush(push.e, push.d));
};

//toto je prakticky kopia toho co je vo frontende, ale musim to riesit takto, kedze to neviem vyriesit cez "funkciu" kde budem posielat cely match
//vzhladom na komplikovanost funkcionality medzi pravym stlpcom a tlv obsahom, kde mame overview, mini overview, pravy stlpec zobrazujuci data, ktore nie su detailom a pod a pod.
//takze to potrebujem handlovat v single handlery, ktory sa zavola raz pri loade resp. otvoreni detailu a job done, mozno casom vymyslim nieco ine, ale teraz mam dost
export function setInfocentrumData(db, match, sportCode, sportId, isPrematchEvent = false) {
    const [betradarStreamId, ufcStreamId, videoStreamId] = getStreamIds(match);
    if (!localStorage.getItem('rightColumnTab')) {
        localStorage.setItem('rightColumnTab', 'STREAM');
    }
    const parsedUfcStream = ufcStreamId?.split(',');
    const streamOnTicket = db.getIn(['infocentrum', 'detail', 'streamOnTicket']);
    return db
        .setIn(
            ['infocentrum', 'detail', 'betradarStreamId'],
            betradarStreamId || (parsedUfcStream && parsedUfcStream[1])
        )
        .setIn(['infocentrum', 'detail', 'sportId'], match.get('sportId'))
        .setIn(['infocentrum', 'detail', 'sportCode'], sportCode)
        .setIn(
            ['infocentrum', 'detail', 'streamTab'],
            isUFC(sportId) ? 'video' : videoStreamId && !isPrematchEvent ? 'video' : 'matchtracker'
        )
        .setIn(['infocentrum', 'detail', 'activeTab'], localStorage.getItem('rightColumnTab') || 'STREAM')
        .setIn(['infocentrum', 'detail', 'eventId'], match.get('id') || match.get('sportEventId'))
        .setIn(['infocentrum', 'detail', 'tournamentId'], match.get('tournamentId'))
        .setIn(['infocentrum', 'detail', 'isPrematchEvent'], isPrematchEvent)
        .setIn(['infocentrum', 'detail', 'match'], fromJS(match))
        .setIn(['infocentrum', 'detail', 'ufcEventId'], parsedUfcStream && parsedUfcStream[0])
        .setIn(['infocentrum', 'detail', 'streamOnTicket'], isVideoStream(match) ? streamOnTicket : false);
}

export function getTabTemplateId(sportEvent) {
    if (!sportEvent) {
        return null;
    }
    const tabTemplates = sportEvent.get('infoCentre');
    const isLive = sportEvent?.get('isLive');
    const expirationDate = Date.parse(sportEvent?.get('expiration'));
    const liveTemplateId = tabTemplates?.get('liveTemplateId');
    const prematchTemplateId = tabTemplates?.get('prematchTemplateId');

    return isLive || expirationDate < now() ? liveTemplateId : prematchTemplateId;
}

export const startInfoCentre = match => db => {
    return match ? setInfocentrumData(db, match, null, match.get('sportId'), true) : db;
};

//TODO: je potrebne zjednotit db path pre data live zapasov, aby sa nemuseli hladat data k live zapasu pre kazdu sekciu v inej path-e
const getLiveMatch = (db, sportCode: string, matchId: string) =>
    db.getIn(['turboLive', 'overview', sportCode, 'matches', `${matchId}`]) ||
    db.getIn(['turboLive', 'overview', 'matches', `${matchId}`]) ||
    db.getIn(['promo', 'live', 'matches', `${matchId}`]);
//handler specificaly for seting right column data in match detail
//has to be based on matchId to avoid rerendering, when data of match are changing
export const startDetailInfocentre = (sportCode: string, matchId: string) => db => {
    const match = getLiveMatch(db, sportCode, matchId);
    return match ? setInfocentrumData(db, match, sportCode, match.get('sportId')) : db;
};

//GRAY MAGIC - it is not dark, nor light, but its kinda mess soo
//overview is based on order of tournaments, so get this order
//based on tournament order, get matches for this tournaments in correct order
//from correctly ordered matches, find first match with streamId,
//if there is none, find first match wit radarId,
//if there is none, get first match from ordered matches
//then set data for right column to "start" infocentre
export const startTLVOverviewInfocentre = sportCode => db => {
    const tournamentIds = db
        .getIn(['turboLive', 'overview', sportCode, 'tournamentIdsByOrder'])
        ?.map(tournament => tournament.id);
    const sortedMatchIds = tournamentIds
        ?.map(tournamentId => db.getIn(['turboLive', 'overview', sportCode, 'matchIdsByTournament', tournamentId]))
        ?.map(matches => matches?.map(match => match.id))
        .flatten();
    const firstMatchWithStream = sortedMatchIds?.find(matchId => {
        const match = db.getIn(['turboLive', 'overview', sportCode, 'matches', matchId]);
        return isVideoStream(match);
    });
    if (firstMatchWithStream) {
        const match = db.getIn(['turboLive', 'overview', sportCode, 'matches', firstMatchWithStream]);
        return match ? setInfocentrumData(db, match, sportCode, match.get('sportId')) : db;
    }
    const firstMatchWithRadar = sortedMatchIds?.find(matchId => {
        const match = db.getIn(['turboLive', 'overview', sportCode, 'matches', matchId]);
        return isRadarStream(match);
    });
    const firstMatch = firstMatchWithRadar
        ? db.getIn(['turboLive', 'overview', sportCode, 'matches', firstMatchWithRadar])
        : db.getIn(['turboLive', 'overview', sportCode, 'matches', sortedMatchIds?.first()]);
    if (firstMatch) {
        const sportId = firstMatch.get('sportId');
        return setInfocentrumData(db, firstMatch, sportCode, sportId);
    }

    return db;
};

// Najde prvy zapas ktory ma stream, ak nema ziadny, najde prvy s MT
// topEvents su selectiony v spravnom poradi, matches niesu spravne zosortovane preto to treba takto robit
export const findFirstMatchWithMT = (topEvents, matches) => {
    let firstMatchWithMTStream = null;
    // prehlada matches v spravnom poradi na zaklade top events selections match ids
    if (topEvents) {
        firstMatchWithMTStream = topEvents?.find(event => {
            const eventId = event.get('matchId');
            const match = matches?.getIn([eventId.toString()]);
            return isVideoStream(match);
        });
        if (!firstMatchWithMTStream) {
            firstMatchWithMTStream = topEvents?.find(event => {
                const eventId = event.get('matchId');
                const match = matches?.getIn([eventId.toString()]);
                return isRadarStream(match) && isBetradarWidgetAvailable(match);
            });
        }
        if (firstMatchWithMTStream) {
            const matchId = firstMatchWithMTStream.get('matchId') || firstMatchWithMTStream.get('sportEventId');
            return matches.get(matchId.toString());
        } else return false;
    }
    // top5 tournaments ma matche zosortovane spravne
    firstMatchWithMTStream = matches?.find(match => isVideoStream(match));
    if (!firstMatchWithMTStream) {
        firstMatchWithMTStream = matches?.find(match => isRadarStream(match) && isBetradarWidgetAvailable(match));
    }
    if (firstMatchWithMTStream) return firstMatchWithMTStream;
    else return false;
};

export const startHomepageInfocentre = () => db => {
    const sportIds = Array.from(db.getIn(['market-statistics', 'top-ranking', 'top5sports']).keys());
    const tournamentIds = db.getIn(['market-statistics', 'top-ranking', 'top5tournamentsIds']);
    let mtStreamMatch = null;
    // searching in top sports and tournaments
    for (let id of sportIds) {
        const matches = db.getIn(['market-statistics', 'top-ranking', 'top5sportMatches', `${id}`]);
        const topEvents = db.getIn(['market-statistics', 'top-ranking', 'top5sports', `${id}`, 'topEvents']);
        mtStreamMatch = findFirstMatchWithMT(topEvents, matches);
        if (mtStreamMatch) {
            return setInfocentrumData(
                db,
                mtStreamMatch,
                mtStreamMatch.get('sportName'),
                mtStreamMatch.get('sportId'),
                !mtStreamMatch.get('isLive')
            );
        }
    }
    for (let id of tournamentIds) {
        const matches = db.getIn(['market-statistics', 'top-ranking', 'top5tournamentMatches', `${id}`]);
        mtStreamMatch = findFirstMatchWithMT(null, matches);
        if (mtStreamMatch) {
            return setInfocentrumData(
                db,
                mtStreamMatch,
                mtStreamMatch.get('sportName'),
                mtStreamMatch.get('sportId'),
                !mtStreamMatch.get('isLive')
            );
        }
    }
    // searching in TOP10/LIVE
    const top10Events = db.getIn(['market-statistics', 'top-ranking', 'top10', 'bets']);
    const top10Matches = db.getIn(['market-statistics', 'top-ranking', 'top10', 'matches']);
    mtStreamMatch = findFirstMatchWithMT(top10Events, top10Matches);
    if (mtStreamMatch) {
        return setInfocentrumData(
            db,
            mtStreamMatch,
            mtStreamMatch.get('sportName'),
            mtStreamMatch.get('sportId'),
            !mtStreamMatch.get('isLive')
        );
    } else {
        const top10LiveEvents = db.getIn(['market-statistics', 'top-ranking', 'top10Live', 'bets']);
        const top10LiveMatches = db.getIn(['market-statistics', 'top-ranking', 'top10Live', 'matches']);
        mtStreamMatch = findFirstMatchWithMT(top10LiveEvents, top10LiveMatches);
        if (mtStreamMatch) {
            return setInfocentrumData(
                db,
                mtStreamMatch,
                mtStreamMatch.get('sportName'),
                mtStreamMatch.get('sportId'),
                !mtStreamMatch.get('isLive')
            );
        }
    }
};

export const setInfocentrumFromTab = (topEvents, matches) => db => {
    // null ide v pripade ak mam matches uz zoradene, cize v pripade turnajov
    const mtStreamMatch = findFirstMatchWithMT(topEvents ? topEvents : null, matches);
    if (mtStreamMatch) {
        return setInfocentrumData(
            db,
            mtStreamMatch,
            mtStreamMatch.get('sportName'),
            mtStreamMatch.get('sportId'),
            !mtStreamMatch.get('isLive')
        );
    }
};

export const startFavouriteInfocentre = db => {
    const overview = db.getIn(['turboLive', 'overview']);
    const sportsWithMatches = overview?.filter(sport => sport.get('matchIdsByStartTime'));

    const availableMatches = sportsWithMatches
        ?.toOrderedMap()
        .reduce((prev, cur) => prev.merge(cur.get('matches')), OrderedMap());

    if (!availableMatches?.size) {
        return db;
    }
    const firstMatchWithStream = availableMatches.find(match => {
        return isVideoStream(match);
    });
    if (firstMatchWithStream) {
        const sportId = firstMatchWithStream.get('sportId');
        const sportCode = db.getIn(['turboLive', 'sports', sportId, 'slug']).substring(1);
        return setInfocentrumData(db, firstMatchWithStream, sportCode, sportId);
    }
    const firstMatchWithRadar = availableMatches.find(match => {
        return isRadarStream(match);
    });
    const firstMatch = firstMatchWithRadar ? firstMatchWithRadar : availableMatches[0];
    if (firstMatch) {
        const sportId = firstMatch.get('sportId');
        const sportCode = db.getIn(['turboLive', 'sports', sportId, 'slug']).substring(1);
        return setInfocentrumData(db, firstMatch, sportCode, sportId);
    }
    return db;
};

export const isRadarStream = (sportEvent: Map<string, any>) => {
    if (!sportEvent?.size) {
        return null;
    }
    const streams = sportEvent.get('streams');

    return streams?.find(
        stream =>
            stream && (stream.get('streamType') === 'LIVE_MATCH_TRACKER' || stream.get('streamType') === 'IMG_UFC')
    );
};

export const isVideoStream = (sportEvent: Map<string, any>) => {
    if (!sportEvent?.size) {
        return null;
    }
    const streams = sportEvent.get('streams');

    return streams?.find(stream => stream && stream.get('streamType') === 'VIDEO');
};

export function getStreamIds(sportEvent) {
    if (!sportEvent) {
        return null;
    }
    const streams = sportEvent.get('streams')?.filter(stream => stream);
    const templateId = getTabTemplateId(sportEvent);

    const MTBetradarStream = streams?.find(stream => stream.get('streamType') === 'LIVE_MATCH_TRACKER');
    const ufcStream = streams?.find(stream => stream.get('streamType') === 'IMG_UFC');
    const videoStream = streams?.find(stream => stream && stream.get('streamType') === 'VIDEO');

    const betradarStreamId = MTBetradarStream && templateId ? MTBetradarStream?.get('streamParamId') : null;
    const ufcStreamId = ufcStream?.get('streamParamId');
    const videoStreamId = videoStream?.get('streamParamId');

    return [betradarStreamId, ufcStreamId, videoStreamId];
}
